import { DemandesV3Error, type DemandesV3Repository } from '../application/DemandesV3Repository'
import {
  leadCategoryNameError,
  type DemandesTriage,
  type LeadCategory,
  type LeadCategoryAssignment,
} from '../domain/leadCategory'

/** Mode démonstration : les quatre catégories initiales, en mémoire, et les positions des demandes de démonstration. */
export function createFixtureDemandesV3Repository(): DemandesV3Repository {
  let categories: LeadCategory[] = [
    {
      id: 'cat-attente',
      name: 'Liste d’attente',
      tone: 'info',
      sortOrder: 0,
      archivedAt: null,
      leadCount: 0,
    },
    {
      id: 'cat-hors-secteur',
      name: 'Hors Secteur',
      tone: 'warning',
      sortOrder: 1,
      archivedAt: null,
      leadCount: 0,
    },
    {
      id: 'cat-pas-interesse',
      name: 'Pas intéressé',
      tone: 'neutral',
      sortOrder: 2,
      archivedAt: null,
      leadCount: 0,
    },
    {
      id: 'cat-rappel',
      name: 'Demander un rappel',
      tone: 'pending',
      sortOrder: 3,
      archivedAt: null,
      leadCount: 0,
    },
  ]
  let assignments: LeadCategoryAssignment[] = []
  const positions = [
    { leadId: 'lead-0000-4000-8000-000000000001', latitude: 45.7807, longitude: -74.0037 },
    { leadId: 'lead-0000-4000-8000-000000000002', latitude: 45.6406, longitude: -73.8336 },
  ]

  const find = (categoryId: string) => {
    const category = categories.find((candidate) => candidate.id === categoryId)
    if (!category) throw new DemandesV3Error('LEAD_CATEGORY_NOT_FOUND')
    return category
  }
  const nextOrder = () => Math.max(-1, ...categories.map((category) => category.sortOrder)) + 1

  return {
    getTriage(): Promise<DemandesTriage> {
      return Promise.resolve({
        categories: categories.map((category) => ({
          ...category,
          leadCount: assignments.filter((assignment) => assignment.categoryId === category.id).length,
        })),
        assignments: [...assignments],
        positions: [...positions],
      })
    },
    createCategory(command) {
      const error = leadCategoryNameError(command.name, categories)
      if (error) return Promise.reject(new DemandesV3Error('LEAD_CATEGORY_NAME_TAKEN'))
      categories = [
        ...categories,
        {
          id: `cat-${String(Date.now())}`,
          name: command.name.trim(),
          tone: command.tone,
          sortOrder: nextOrder(),
          archivedAt: null,
          leadCount: 0,
        },
      ]
      return Promise.resolve()
    },
    updateCategory(command) {
      find(command.categoryId)
      categories = categories.map((category) =>
        category.id === command.categoryId
          ? { ...category, name: command.name.trim(), tone: command.tone }
          : category,
      )
      return Promise.resolve()
    },
    setCategoryArchived(command) {
      find(command.categoryId)
      categories = categories.map((category) =>
        category.id === command.categoryId
          ? {
              ...category,
              archivedAt: command.archived ? (category.archivedAt ?? new Date().toISOString()) : null,
              sortOrder: command.archived ? category.sortOrder : nextOrder(),
            }
          : category,
      )
      return Promise.resolve()
    },
    reorderCategories(categoryIds) {
      categories = categories.map((category) => {
        const index = categoryIds.indexOf(category.id)
        return index < 0 ? category : { ...category, sortOrder: index }
      })
      return Promise.resolve()
    },
    assignCategory(command) {
      assignments = assignments.filter((assignment) => assignment.leadId !== command.leadId)
      if (command.categoryId) {
        find(command.categoryId)
        assignments.push({
          leadId: command.leadId,
          categoryId: command.categoryId,
          assignedAt: new Date().toISOString(),
        })
      }
      return Promise.resolve()
    },
    restoreLead() {
      return Promise.reject(new DemandesV3Error('LEAD_RESTORE_UNAVAILABLE'))
    },
  }
}
